import { useEffect, useRef, useState } from "react";
import { geocode, type GeocodeHit } from "../lib/api";
import { useMap } from "../map/useMapStore";
import { withinIdf } from "../map/bounds";
import { jumpTo } from "../map/jumpTo";

/**
 * Look up a specific address and drop the map on it.
 *
 * The point of the app is deciding between actual flats, and an actual flat is
 * an address — not a neighbourhood.
 */

/** Short enough to feel immediate, long enough to skip mid-word keystrokes. */
const DEBOUNCE_MS = 120;

/**
 * Answers already fetched this session.
 *
 * Backspacing walks back through queries that were just typed; without this,
 * every deleted character costs another round trip to redisplay a list the
 * browser had a moment ago. The key carries the bias point as well as the
 * query, since the same words ranked from a different part of the map are a
 * different answer.
 */
const cache = new Map<string, GeocodeHit[]>();

export function AddressSearch() {
  const map = useMap();
  const [q, setQ] = useState("");
  const [hits, setHits] = useState<GeocodeHit[]>([]);
  const [open, setOpen] = useState(false);
  const [cursor, setCursor] = useState(0);
  const timer = useRef<number | undefined>(undefined);
  const abort = useRef<AbortController | null>(null);
  const box = useRef<HTMLDivElement>(null);
  const pending = useRef<(() => void) | null>(null);

  useEffect(() => {
    window.clearTimeout(timer.current);
    const query = q.trim();
    if (query.length < 3) {
      setHits([]);
      setOpen(false);
      return;
    }

    // Bias to what the user is looking at, so an address in the suburb on
    // screen outranks the same street name at the other end of the country.
    const centre = map?.getCenter();
    const near = centre ? { lon: centre.lng, lat: centre.lat } : undefined;
    const key = near
      ? `${query}@${near.lon.toFixed(1)},${near.lat.toFixed(1)}`
      : query;

    const cached = cache.get(key);
    if (cached) {
      setHits(cached);
      setCursor(0);
      setOpen(true);
      return;
    }

    timer.current = window.setTimeout(() => {
      abort.current?.abort();
      const controller = new AbortController();
      abort.current = controller;
      geocode(query, { near, signal: controller.signal })
        .then((all) => {
          // Anything outside the region is unreachable: the map is clamped to
          // Île-de-France, so picking it would move nothing and look broken.
          const h = all.filter((x) => withinIdf(x.lon, x.lat));
          cache.set(key, h);
          setHits(h);
          setCursor(0);
          setOpen(true);
        })
        .catch((err) => {
          if ((err as Error).name !== "AbortError") setHits([]);
        });
    }, DEBOUNCE_MS);

    return () => window.clearTimeout(timer.current);
  }, [q, map]);

  // Clicking the map, or anywhere else, should put the suggestions away.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      if (!box.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("pointerdown", onDown);
    return () => document.removeEventListener("pointerdown", onDown);
  }, [open]);

  const choose = (h: GeocodeHit) => {
    setQ(h.label);
    setOpen(false);
    if (!map) return;

    // Drop a pick that has not landed yet, so choosing a second address while
    // the first is still flying cannot select the place you changed your mind
    // about a moment after arriving at the one you wanted.
    pending.current?.();
    pending.current = jumpTo(map, {
      lon: h.lon,
      lat: h.lat,
      zoom: 15.5,
      // A shortlist of flats wants the street address, not the name of the
      // statistical zone the click happened to land in.
      onArrive: (selection) => ({ ...selection, address: h.label }),
    });
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      setOpen(false);
      return;
    }
    if (!open || hits.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setCursor((c) => (c + 1) % hits.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setCursor((c) => (c - 1 + hits.length) % hits.length);
    } else if (e.key === "Enter") {
      e.preventDefault();
      choose(hits[cursor]);
    }
  };

  return (
    <div className="relative" ref={box}>
      <input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onFocus={() => hits.length > 0 && setOpen(true)}
        onKeyDown={onKeyDown}
        placeholder="Search an address…"
        aria-label="Search an address"
        autoComplete="off"
        className="w-full rounded-lg border border-line bg-surface px-2.5 py-1.5 text-[12px] outline-none focus:border-accent"
      />
      {open && hits.length > 0 && (
        <ul className="absolute left-0 right-0 top-full z-20 mt-1 overflow-hidden rounded-lg border border-line bg-surface shadow-lg">
          {hits.map((h, i) => (
            <li key={`${h.lon},${h.lat}`}>
              <button
                onClick={() => choose(h)}
                onMouseEnter={() => setCursor(i)}
                className={`block w-full px-2.5 py-1.5 text-left text-[12px] ${
                  i === cursor ? "bg-surface-alt" : ""
                }`}
              >
                {h.label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
